<template>
  <el-container class="chat-page">
    <!-- 左侧聊天区域 -->
    <el-container class="chat-box">
      <!-- 容器管理组件 - 云电脑远程工作台 -->
      <container-manager v-if="isPCTerminalChat" ref="containerManager"></container-manager>
      
      <!-- 人工聊天 -->
      <chat-box v-if="chatStore.activeChat && !isAIChat" :chat="chatStore.activeChat" @message-sent="onMessageSent">
      </chat-box>

      <!-- 空状态 -->
      <div v-if="!chatStore.activeChat && !selectedAIChat" class="empty-state">
        <div class="empty-content">
          <div class="empty-icon">💬</div>
          <p>像使用微信一样使用AI...</p>
        </div>
      </div>
    </el-container>
  </el-container>
</template>

<script>
/* global IChatWorkspaceService, IFileService, INotificationService, URIHelper, VSBufferHelper */

import ChatItem from "../components/chat/ChatItem.vue";
import ChatBox from "../components/chat/ChatBox.vue";
import ContainerManager from "../components/chat/ContainerManager.vue";
// import ThinkingChain from "../components/chat/ThinkingChain.vue";
// import ThinkingDashboard from "../components/chat/ThinkingDashboard.vue";
// import AIChatSimplified from "../components/chat/AIChatSimplified.vue";
import { log } from "@/utils/logManager.js";

export default {
  name: "EditorChat",
  components: {
    ChatItem,
    ChatBox,
    ContainerManager,
  },
  props: {
    chatData: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      searchText: "",
      messageContent: "",
      group: {},
      groupMembers: [],

      // 思维面板相关
      showThinkingPanel: false,
      activeTab: 'chain',
      currentSessionId: '',

      // AI聊天相关
      selectedAIChat: null,
      isAIChat: false,
      
      // PC终端会话检测
      isPCTerminalChat: false,

      // 🆕 拖拽相关状态
      dragHintElement: null,
      processingMessage: null,
      globalDragListeners: null, // 🔧 统一的全局拖拽监听器

      // 🔧 统一会话服务监听器
      unifiedSessionUnsubscribe: null,

      // 防重复激活状态
      lastActivatedSessionId: null,

      // 标签页配置
      tabs: [
        { key: 'chain', label: '思维链', icon: '🧠' },
        { key: 'dashboard', label: '仪表盘', icon: '📊' },
        { key: 'assistant', label: 'AI助手', icon: '🤖' },
        { key: 'settings', label: '设置', icon: '⚙️' }
      ],

      // AI助手相关
      assistantStatus: 'active', // active, idle, analyzing
      feedbackText: '',
      aiInsights: [
        {
          id: 'insight_1',
          icon: '💡',
          title: '对话模式识别',
          description: '检测到用户倾向于技术性讨论，建议提供更详细的技术解释',
          priority: 'medium'
        },
        {
          id: 'insight_2',
          icon: '⚡',
          title: '回复效率优化',
          description: '可以通过预加载常见问题答案来提升响应速度',
          priority: 'low'
        }
      ],

      // 设置相关
      settings: {
        showRealtime: true,
        autoExpand: true,
        showConfidence: true,
        analysisDepth: 'detailed',
        updateInterval: '3000',
        exportFormat: 'json'
      }
    }
  },
  computed: {
    chatStore() {
      return this.$store.state.chatStore;
    },
    loading() {
      return this.chatStore.loadingGroupMsg || this.chatStore.loadingPrivateMsg
    },
    assistantStatusText() {
      const statusMap = {
        active: '🟢 活跃',
        idle: '🟡 空闲',
        analyzing: '🔵 分析中'
      }
      return statusMap[this.assistantStatus] || '未知'
    }
  },
  created() {
    this.loadSettings()
  },
  methods: {
    
    // 检测当前会话是否为PC终端
    detectPCTerminalChat() {
      if (!this.chatStore.activeChat) {
        this.isPCTerminalChat = false;
        return;
      }

      // 🔥 智能体会话不显示云电脑管理面板
      const isAgentChat = this.chatStore.activeChat.type === 'AGENT' ||
                         (this.chatStore.activeChat.targetId && String(this.chatStore.activeChat.targetId).includes('agent_'));

      if (isAgentChat) {
        this.isPCTerminalChat = false;
        return;
      }

      const targetId = this.chatStore.activeChat.targetId;
      // 简化判断：只要targetId包含"_3"就是PC终端会话（转为字符串以支持数字类型）
      this.isPCTerminalChat = targetId ? String(targetId).includes('_3') : false;
    },
    
    onActiveItem(index) {
      this.$store.commit("activeChat", index);

      // 切换到人类聊天模式
      this.isAIChat = false;
      this.selectedAIChat = null;
      
      // 检测是否为PC终端会话
      this.detectPCTerminalChat();
    },

    onDelItem(index) {
      this.$store.commit("removeChat", index);
    },

    onTop(chatIdx) {
      this.$store.commit("moveTop", chatIdx);
    },

    // 获取当前聊天ID
    getCurrentChatId() {
      if (this.isAIChat && this.selectedAIChat) {
        return this.selectedAIChat.aiThreadId || this.selectedAIChat.id;
      }
      if (this.chatStore.activeChat) {
        return this.chatStore.activeChat.id || this.chatStore.activeChat.targetId;
      }
      return this.currentSessionId;
    },

    // 处理AI聊天选择（从InboxSession传来）
    onAIChatSelected(aiSession) {
      log.info('EditorChat', '收到AI聊天选择:', aiSession.showName);

      this.selectedAIChat = aiSession;
      this.isAIChat = true;

      // 清除人类聊天选择
      this.$store.commit("clearActiveChat");
      
      // AI聊天不显示容器管理
      this.isPCTerminalChat = false;
    },

    // 🌟 处理确保面板可见事件
    handleEnsurePanelVisible(eventData) {
      console.log('[EditorChat] 🔧 收到确保面板可见事件:', eventData);

      const { sessionType, sessionData, reason } = eventData;

      // 检查是否需要确保面板可见
      if (!this.shouldEnsurePanelVisible(sessionType, sessionData)) {
        console.log('[EditorChat] 🔧 无需确保面板可见，条件不满足');
        return;
      }

      // 确保聊天记录面板可见
      this.ensureChatRecordVisible(sessionType, sessionData, reason);
    },

    // 🌟 检查是否需要确保面板可见
    shouldEnsurePanelVisible(sessionType, sessionData) {
      // 如果当前没有活跃会话，则需要确保面板可见
      if (sessionType === 'IM') {
        return !this.chatStore.activeChat || this.chatStore.activeChat.targetId !== (sessionData.targetId || sessionData.id);
      } else if (sessionType === 'AI') {
        return !this.selectedAIChat || this.selectedAIChat.id !== sessionData.id;
      }
      return true;
    },

    // 🌟 确保聊天记录可见
    ensureChatRecordVisible(sessionType, sessionData, reason) {
      console.log(`[EditorChat] 🔧 确保${sessionType}聊天记录可见:`, sessionData?.showName || sessionData?.name);

      // 根据会话类型处理
      if (sessionType === 'IM') {
        // 确保IM聊天记录可见
        this.ensureIMChatVisible(sessionData, reason);
      } else if (sessionType === 'AI') {
        // 确保AI聊天记录可见  
        this.ensureAIChatVisible(sessionData, reason);
      }

      // 强制更新Vue响应式系统
      this.$nextTick(() => {
        console.log('[EditorChat] 🔧 Vue响应式更新完成，面板应该已可见');
      });
    },

    // 🌟 确保IM聊天可见
    ensureIMChatVisible(sessionData, reason) {
      // 如果当前不是IM模式，切换到IM模式
      if (this.isAIChat) {
        this.isAIChat = false;
        this.selectedAIChat = null;
        console.log('[EditorChat] 🔧 已切换到IM模式');
      }

      // 延迟确保数据已加载
      setTimeout(() => {
        if (this.chatStore.activeChat &&
          (this.chatStore.activeChat.targetId === sessionData.targetId ||
            this.chatStore.activeChat.id === sessionData.id)) {
          console.log('[EditorChat] ✅ IM聊天记录已可见，无需额外处理');
        } else {
          console.log('[EditorChat] ⚠️ IM聊天记录可能未正确加载，发送刷新事件');
          this.$bus && this.$bus.$emit('chat:force-refresh');
        }
      }, 100);
    },

    // 🌟 确保AI聊天可见
    ensureAIChatVisible(sessionData, reason) {
      // 如果当前不是AI模式，切换到AI模式
      if (!this.isAIChat) {
        this.isAIChat = true;
        this.$store.commit("clearActiveChat");
        console.log('[EditorChat] 🔧 已切换到AI模式');
      }

      // 确保选中正确的AI会话
      if (!this.selectedAIChat || this.selectedAIChat.id !== sessionData.id) {
        this.selectedAIChat = sessionData;
        console.log('[EditorChat] 🔧 已设置活跃AI会话:', sessionData.showName);
      }

      // 延迟确保AI聊天界面已渲染
      setTimeout(() => {
        console.log('[EditorChat] ✅ AI聊天记录应该已可见');
      }, 100);
    },

    // 🔧 已移除onHumanChatSelected方法，现在直接通过统一会话服务处理

    // 跳转到专业模式
    jumpToProMode(messageId) {
      console.log('跳转到React专业模式:', messageId);

      // 这里应该通知React侧打开专业模式
      // 可以通过postMessage或者专门的API
      if (window.postMessage) {
        window.postMessage({
          type: 'OPEN_AI_PRO_MODE',
          messageId,
          threadId: this.selectedAIChat?.aiThreadId
        }, '*');
      }

      // 也可以通过VSCode API
      if (window.SharedServicesAccessor && window.SharedServicesAccessor.openAIProMode) {
        try {
          window.SharedServicesAccessor.openAIProMode({
            messageId,
            threadId: this.selectedAIChat?.aiThreadId
          });
        } catch (error) {
          console.error('打开AI专业模式失败:', error);
        }
      }
    },

    // 下载构件
    async downloadArtifact(artifact) {
      console.log('下载构件到工作区:', artifact);

      // 这里应该调用实际的下载API
      try {
        // 模拟下载过程
        this.$message?.success(`正在下载：${artifact.title}`);

        // 可以调用VSCode API下载到工作区
        if (window.SharedServicesAccessor && window.SharedServicesAccessor.downloadToWorkspace) {
          await window.SharedServicesAccessor.downloadToWorkspace(artifact);
          this.$message?.success(`已下载到工作区：${artifact.title}`);
        }
      } catch (error) {
        this.$message?.error('下载失败');
        console.error('下载构件失败:', error);
      }
    },

    // 切换思维面板显示
    toggleThinkingPanel() {
      this.showThinkingPanel = !this.showThinkingPanel

      // 如果隐藏面板，暂停思维链分析
      if (!this.showThinkingPanel) {
        this.pauseThinkingAnalysis()
      } else {
        this.resumeThinkingAnalysis()
      }
    },

    // 暂停思维链分析
    pauseThinkingAnalysis() {
      console.log('暂停思维链分析')
      this.assistantStatus = 'idle'
    },

    // 恢复思维链分析
    resumeThinkingAnalysis() {
      console.log('恢复思维链分析')
      this.assistantStatus = 'active'
    },

    // 实时模式变化处理
    onRealtimeModeChanged(enabled) {
      console.log('实时模式变化:', enabled)
      this.settings.showRealtime = enabled
    },

    // 仪表盘数据刷新处理
    onDashboardDataRefreshed(data) {
      console.log('仪表盘数据已刷新:', data)
    },

    // 思维链选择处理
    onChainSelected(chainId) {
      console.log('选择思维链:', chainId)
      this.activeTab = 'chain'
    },

    // AI助手操作
    analyzeCurrentConversation() {
      this.assistantStatus = 'analyzing'

      // 模拟分析过程
      setTimeout(() => {
        this.assistantStatus = 'active'
        this.addInsight({
          id: `insight_${Date.now()}`,
          icon: '🔍',
          title: '对话分析完成',
          description: this.isAIChat ?
            '当前AI对话显示复杂的技术讨论模式' :
            '当前对话显示用户关注代码质量和性能优化',
          priority: 'high'
        })
      }, 2000)
    },

    suggestResponse() {
      console.log('生成回复建议')
      this.addInsight({
        id: `insight_${Date.now()}`,
        icon: '💬',
        title: '回复建议',
        description: this.isAIChat ?
          '建议简化AI回复，突出关键信息' :
          '建议提供具体的代码示例和性能对比数据',
        priority: 'medium'
      })
    },

    optimizeThinking() {
      console.log('优化思维链')
      this.addInsight({
        id: `insight_${Date.now()}`,
        icon: '⚡',
        title: '思维链优化',
        description: '已优化决策分支，提升思维效率15%',
        priority: 'low'
      })
    },

    exportInsights() {
      const insights = {
        sessionId: this.currentSessionId,
        chatType: this.isAIChat ? 'ai' : 'human',
        chatInfo: this.isAIChat ? this.selectedAIChat : this.chatStore.activeChat,
        timestamp: Date.now(),
        insights: this.aiInsights,
        settings: this.settings
      }

      const dataStr = JSON.stringify(insights, null, 2)
      const blob = new Blob([dataStr], { type: 'application/json' })
      const url = URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = url
      a.download = `ai-insights-${Date.now()}.json`
      a.click()

      URL.revokeObjectURL(url)
    },

    // 添加洞察
    addInsight(insight) {
      this.aiInsights.unshift(insight)

      // 限制洞察数量
      if (this.aiInsights.length > 10) {
        this.aiInsights = this.aiInsights.slice(0, 10)
      }
    },

    // 提交反馈
    submitFeedback() {
      if (!this.feedbackText.trim()) {
        this.$message?.warning('请输入反馈内容')
        return
      }

      console.log('提交反馈:', this.feedbackText)

      // 模拟提交
      this.$message?.success('反馈已提交，感谢您的建议！')
      this.feedbackText = ''
    },

    // 设置相关
    updateSettings() {
      console.log('更新设置:', this.settings)
      localStorage.setItem('thinking_settings', JSON.stringify(this.settings))
    },

    resetSettings() {
      this.settings = {
        showRealtime: true,
        autoExpand: true,
        showConfidence: true,
        analysisDepth: 'detailed',
        updateInterval: '3000',
        exportFormat: 'json'
      }
      this.updateSettings()
      this.$message?.success('设置已重置')
    },

    saveSettings() {
      this.updateSettings()
      this.$message?.success('设置已保存')
    },

    loadSettings() {
      const savedSettings = localStorage.getItem('thinking_settings')
      if (savedSettings) {
        try {
          this.settings = { ...this.settings, ...JSON.parse(savedSettings) }
        } catch (e) {
          console.error('加载设置失败:', e)
        }
      }
    },

    // 事件监听
    setupEventListeners() {
      // 监听聊天消息
      this.$bus && this.$bus.$on('chat:message:sent', this.onMessageSent)
      this.$bus && this.$bus.$on('chat:message:received', this.onMessageReceived)

      // 监听思维链事件
      this.$bus && this.$bus.$on('thinking:chain:updated', this.onThinkingChainUpdated)

      // 监听AI聊天选择事件
      this.$bus && this.$bus.$on('ai:chat:selected', this.onAIChatSelected)

      // 🌟 监听聊天面板可见确保事件
      this.$bus && this.$bus.$on('chat:ensure-panel-visible', this.handleEnsurePanelVisible)

      // 🔧 测试事件监听
      this.$bus && this.$bus.$on('test:panel:debug', (data) => {
        console.log('[EditorChat] 🔧 收到测试事件:', data);
      })

      // 🔧 直接监听统一会话服务事件，移除human:chat:selected自定义事件
      this.setupUnifiedSessionListener()
    },

    cleanupEventListeners() {
      if (this.$bus) {
        this.$bus.$off('chat:message:sent', this.onMessageSent)
        this.$bus.$off('chat:message:received', this.onMessageReceived)
        this.$bus.$off('thinking:chain:updated', this.onThinkingChainUpdated)
        this.$bus.$off('ai:chat:selected', this.onAIChatSelected)
        this.$bus.$off('chat:ensure-panel-visible', this.handleEnsurePanelVisible)
      }

      // 清理统一会话服务监听器
      if (this.unifiedSessionUnsubscribe) {
        this.unifiedSessionUnsubscribe()
        this.unifiedSessionUnsubscribe = null
      }
    },

    // 消息发送处理
    onMessageSent(message) {
      console.log('消息已发送:', message)
      
      // 如果容器管理面板存在且未折叠，则自动折叠
      if (this.$refs.containerManager && !this.$refs.containerManager.isCollapsed) {
        console.log('[EditorChat] 发送消息，自动折叠容器管理面板');
        this.$refs.containerManager.toggleCollapse();
      }

      // 触发AI思维分析
      if (this.settings.showRealtime) {
        this.triggerThinkingAnalysis('user_message', message)
      }
    },

    // 消息接收处理
    onMessageReceived(message) {
      console.log('消息已接收:', message)

      // 如果是AI回复，可以显示思维过程
      if (message.type === 'ai_response' && this.settings.showRealtime) {
        this.triggerThinkingAnalysis('ai_response', message)
      }
    },

    // 思维链更新处理
    onThinkingChainUpdated(data) {
      console.log('思维链已更新:', data)
    },

    // 触发思维分析
    triggerThinkingAnalysis(type, data) {
      console.log('触发思维分析:', type, data)

      // 发送思维开始事件
      this.$bus && this.$bus.$emit('ai:thinking:start', {
        type,
        data,
        preview: this.isAIChat ? '正在分析AI对话内容...' : '正在分析消息内容...'
      })

      // 模拟思维过程
      setTimeout(() => {
        this.$bus && this.$bus.$emit('ai:thinking:node', {
          id: `node_${Date.now()}`,
          type: 'analysis',
          title: this.isAIChat ? 'AI对话分析' : '消息分析',
          description: this.isAIChat ?
            '分析AI回复的复杂度和用户满意度' :
            '分析消息的语义和用户意图',
          confidence: 0.85,
          duration: 1200
        })
      }, 1000)

      setTimeout(() => {
        this.$bus && this.$bus.$emit('ai:thinking:complete')
      }, 3000)
    },

    // ===== 文件自动保存到聊天工作区功能 =====

    // 文件发送处理 - 使用注入的服务
    async handleFileSend(fileData) {
      console.log('[EditorChat] handleFileSend called with:', fileData);

      try {

        console.log('[EditorChat] VSCode services obtained successfully');

        const currentSession = this.getCurrentSessionInfo();
        console.log('[EditorChat] Current session info:', currentSession);

        await this.saveFileToSession(fileData, currentSession);

        window.vscodeServices?.get('INotificationService').info(`文件已保存到工作区: ${fileData.file.name}`);

        console.log('[EditorChat] File saved successfully to workspace');
      } catch (error) {
        console.error('[EditorChat] 文件保存失败:', error);
        window.vscodeServices?.get('INotificationService').error(`文件保存失败: ${error.message}`);
      }
    },

    // 获取当前会话信息
    getCurrentSessionInfo() {
      if (this.isAIChat && this.selectedAIChat) {
        return {
          id: this.selectedAIChat.aiThreadId || this.selectedAIChat.id,
          targetId: this.selectedAIChat.aiThreadId || this.selectedAIChat.id,
          type: 'PRIVATE', // 统一为IM系统的格式
          name: this.selectedAIChat.showName || 'AI对话',
          showName: this.selectedAIChat.showName || 'AI对话',
          participants: ['AI']
        };
      }

      if (this.chatStore.activeChat) {
        const activeChat = this.chatStore.activeChat;
        const sessionInfo = {
          id: activeChat.id || activeChat.targetId,
          targetId: activeChat.targetId,
          type: activeChat.type || 'PRIVATE', // 使用聊天实际类型
          name: activeChat.showName || '聊天',
          showName: activeChat.showName || '聊天',
          headImage: activeChat.headImage || '',
          participants: [activeChat.targetId]
        };

        // 🔧 处理终端会话的特殊字段
        if (activeChat.isTerminalChat || activeChat.isTerminalInbox) {
          sessionInfo.isTerminalChat = activeChat.isTerminalChat;
          sessionInfo.isTerminalInbox = activeChat.isTerminalInbox;
          sessionInfo.senderTerminal = activeChat.senderTerminal;
          sessionInfo.targetTerminal = activeChat.targetTerminal;
          sessionInfo.receivingTerminal = activeChat.receivingTerminal; // 兼容旧字段
          
          console.log('[EditorChat] 🔧 终端会话信息:', {
            isTerminalChat: sessionInfo.isTerminalChat,
            senderTerminal: sessionInfo.senderTerminal,
            targetTerminal: sessionInfo.targetTerminal
          });
        }

        return sessionInfo;
      }

      // 默认会话 - 创建一个基础的私聊会话
      const defaultSessionId = this.currentSessionId || `session_${Date.now()}`;
      return {
        id: defaultSessionId,
        targetId: defaultSessionId,
        type: 'PRIVATE',
        name: '当前会话',
        showName: '当前会话',
        headImage: '',
        participants: []
      };
    },

    // 🎯 使用统一的文件上传入口
    async saveFileToSession(fileData, sessionInfo) {

      try {
        const file = fileData.file;
        console.log('[EditorChat] 开始文件上传:', { fileName: file.name, size: file.size, type: file.type });

        // 🚀 使用统一的uploadFiles方法
        const results = await window.vscodeServices?.get('IChatWorkspaceService').uploadFiles([file]);

        if (results && results.length > 0 && results[0].success) {
          console.log('[EditorChat] ✅ 文件上传成功:', results[0]);
          return results[0].filePath;
        } else {
          const error = results?.[0]?.error || '文件上传失败';
          console.error('[EditorChat] ❌ 文件上传失败:', error);
          throw new Error(error);
        }
      } catch (error) {
        console.error('[EditorChat] 文件上传异常:', error);
        throw error;
      }
    },

    // 生成会话路径
    generateSessionPath(sessionInfo) {
      const sanitizedName = sessionInfo.name.replace(/[<>:"/\\|?*]/g, '_');
      const timestamp = new Date().toISOString().slice(0, 10);
      return `chat_sessions/${sessionInfo.type}/${sanitizedName}_${sessionInfo.id}_${timestamp}`;
    },

    // 生成安全文件名
    generateSafeFileName(originalName) {
      // 只清理非法字符，不添加时间戳，重复文件名处理由SharedServicesAccessor负责
      return originalName.replace(/[<>:"/\\|?*\u0000-\u001f]/g, '_');
    },

    // 读取文件内容
    readFileContent(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
    },


    // 🛡️ 保护VSCode服务对象，防止被Vue响应式系统处理
    protectVSCodeService(service, serviceName = 'service') {
      if (!service || typeof service !== 'object') {
        return service;
      }

      try {
        // 创建一个代理来防止Vue访问内部Symbol属性
        return new Proxy(service, {
          get(target, prop) {
            // 阻止访问Symbol.toStringTag等可能导致问题的属性
            if (typeof prop === 'symbol' || prop === Symbol.toStringTag) {
              console.debug('[EditorChat] 🛡️ 阻止访问 Symbol 属性:', prop.toString());
              return undefined;
            }

            try {
              const value = target[prop];

              // 如果是函数，绑定this上下文
              if (typeof value === 'function') {
                return value.bind(target);
              }

              return value;
            } catch (error) {
              console.warn(`[EditorChat] 🛡️ 访问 ${serviceName}.${prop} 失败:`, error);
              return undefined;
            }
          },

          // 防止Vue遍历对象属性
          ownKeys(target) {
            try {
              return Object.getOwnPropertyNames(target).filter(key =>
                typeof key === 'string' && !key.startsWith('_')
              );
            } catch (error) {
              console.warn(`[EditorChat] 🛡️ 获取 ${serviceName} 属性列表失败:`, error);
              return [];
            }
          },

          // 阻止属性描述符访问
          getOwnPropertyDescriptor(target, prop) {
            if (typeof prop === 'symbol') {
              return undefined;
            }
            try {
              return Object.getOwnPropertyDescriptor(target, prop);
            } catch (error) {
              return undefined;
            }
          }
        });
      } catch (error) {
        console.warn(`[EditorChat] 🛡️ 创建 ${serviceName} 保护代理失败:`, error);
        return service;
      }
    },

    // 🆕 统一的文件拖拽支持 - 父级组件管理
    setupFileDragDrop() {
      console.log('[EditorChat] 🎯 设置统一的文件拖拽管理（父级组件统一处理）');

      // 全局document监听器 - 智能分发拖拽事件
      this.globalDragListeners = {
        handleDragEnter: (e) => {
          // 检查拖拽目标是否在Vue聊天组件区域内
          if (this.isDragTargetInChatArea(e.target)) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            console.log('[EditorChat] 🎯 拦截dragenter - 目标在聊天区域');
            this.handleDragEnter(e);
          }
          // 否则不处理，让VSCode工作区正常工作
        },

        handleDragOver: (e) => {
          if (this.isDragTargetInChatArea(e.target)) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            e.dataTransfer.dropEffect = 'copy';
            console.log('[EditorChat] 🎯 拦截dragover - 目标在聊天区域');
          }
        },

        handleDragLeave: (e) => {
          if (this.isDragTargetInChatArea(e.target)) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            console.log('[EditorChat] 🎯 拦截dragleave - 目标在聊天区域');
            this.handleDragLeave(e);
          }
        },

        handleDrop: (e) => {
          if (this.isDragTargetInChatArea(e.target)) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            console.log('[EditorChat] 🎯 拦截drop - 目标在聊天区域，完全阻止VSCode处理');

            // 智能分发到对应的组件处理
            this.handleFileDropToComponent(e);
          }
        }
      };

      // 全局监听器使用capture模式，确保优先级最高
      document.addEventListener('dragenter', this.globalDragListeners.handleDragEnter, true);
      document.addEventListener('dragover', this.globalDragListeners.handleDragOver, true);
      document.addEventListener('dragleave', this.globalDragListeners.handleDragLeave, true);
      document.addEventListener('drop', this.globalDragListeners.handleDrop, true);

      console.log('[EditorChat] ✅ 统一拖拽管理已设置 - 智能分发到聊天区域');
    },

    // 🎯 智能判断：拖拽目标是否在聊天区域内
    isDragTargetInChatArea(target) {
      if (!this.$el || !target) return false;

      // 检查是否在EditorChat组件内
      const isInEditorChat = this.$el.contains(target);

      // 进一步检查是否在聊天相关区域内
      if (isInEditorChat) {
        // 查找具体的聊天区域
        const chatBox = this.$el.querySelector('.chat-box');
        const chatInput = this.$el.querySelector('.chat-input-area');
        const dragOverlay = this.$el.querySelector('.drag-overlay');

        // 如果在这些区域内，则认为是聊天区域
        const isInChatBox = chatBox && chatBox.contains(target);
        const isInChatInput = chatInput && chatInput.contains(target);
        const isInDragOverlay = dragOverlay && dragOverlay.contains(target);

        return isInChatBox || isInChatInput || isInDragOverlay;
      }

      return false;
    },

    // 🎯 智能分发文件拖拽到具体组件
    handleFileDropToComponent(event) {
      const target = event.target;
      const files = event.dataTransfer.files;

      console.log('[EditorChat] 🎯 智能分发文件拖拽，文件数量:', files.length);

      // 判断是否拖拽到ChatInput区域
      const chatInputArea = this.$el.querySelector('.chat-input-area');
      if (chatInputArea && chatInputArea.contains(target)) {
        console.log('[EditorChat] 🎯 分发到ChatInput组件处理');

        // 查找ChatInput组件实例并调用其处理方法
        const chatBox = this.$refs.chatBox || this.$children.find(child => child.$options.name === 'chatPrivate');
        if (chatBox && chatBox.$refs && chatBox.$refs.chatInputEditor) {
          const chatInput = chatBox.$refs.chatInputEditor;
          if (chatInput && typeof chatInput.handleDroppedFiles === 'function') {
            // 设置拖拽状态
            chatInput.isDragOver = false;
            chatInput.dragCounter = 0;

            // 调用ChatInput的文件处理方法
            chatInput.handleDroppedFiles(files);
            return;
          }
        }
      }

      // 否则使用EditorChat的通用文件处理
      console.log('[EditorChat] 🎯 使用EditorChat通用文件处理');
      this.handleFileDrop(event);
    },

    // 🔧 移除全局拖拽监听器
    removeGlobalDragListeners() {
      if (this.globalDragListeners) {
        console.log('[EditorChat] 🧹 清理统一拖拽监听器');
        document.removeEventListener('dragenter', this.globalDragListeners.handleDragEnter, true);
        document.removeEventListener('dragover', this.globalDragListeners.handleDragOver, true);
        document.removeEventListener('dragleave', this.globalDragListeners.handleDragLeave, true);
        document.removeEventListener('drop', this.globalDragListeners.handleDrop, true);
        this.globalDragListeners = null;
      }
    },

    // 处理拖拽悬停
    handleDragOver(event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation(); // 强制阻止其他监听器
      event.dataTransfer.dropEffect = 'copy';

      console.log('[EditorChat] Vue组件拦截dragover事件，阻止VSCode处理');
    },

    // 处理拖拽进入
    handleDragEnter(event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation(); // 强制阻止其他监听器

      console.log('[EditorChat] Vue组件拦截dragenter事件，阻止VSCode处理');

      // 检查是否有文件
      if (this.hasFiles(event.dataTransfer)) {
        this.showDragHint();
        this.addDragOverClass();
      }
    },

    // 处理拖拽离开
    handleDragLeave(event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation(); // 强制阻止其他监听器

      console.log('[EditorChat] Vue组件拦截dragleave事件，阻止VSCode处理');

      // 检查是否真正离开了拖拽区域
      if (!this.$el.contains(event.relatedTarget)) {
        this.hideDragHint();
        this.removeDragOverClass();
      }
    },

    // 🎯 核心：处理文件拖拽放下
    async handleFileDrop(event) {
      // 立即阻止事件传播，确保VSCode组件不会处理
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      console.log('[EditorChat] 🎯 Vue组件接收到拖拽事件，立即阻止传播');
      console.log('[EditorChat] Event target:', event.target);
      console.log('[EditorChat] Event currentTarget:', event.currentTarget);

      this.hideDragHint();
      this.removeDragOverClass();

      console.log('[EditorChat] Drop event received:', {
        dataTransfer: event.dataTransfer,
        types: Array.from(event.dataTransfer.types),
        files: event.dataTransfer.files,
        filesLength: event.dataTransfer.files.length,
        items: event.dataTransfer.items,
        itemsLength: event.dataTransfer.items ? event.dataTransfer.items.length : 0
      });

      const files = Array.from(event.dataTransfer.files);

      if (files.length === 0) {
        console.log('[EditorChat] No files in drop event, checking items...');

        // 尝试从items中获取文件
        if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
          const itemFiles = [];
          for (let i = 0; i < event.dataTransfer.items.length; i++) {
            const item = event.dataTransfer.items[i];
            console.log('[EditorChat] Item:', item.kind, item.type);

            if (item.kind === 'file') {
              const file = item.getAsFile();
              if (file) {
                itemFiles.push(file);
              }
            }
          }

          if (itemFiles.length > 0) {
            console.log('[EditorChat] Found files in items:', itemFiles);
            await this.processDroppedFiles(itemFiles);
            return;
          }
        }

        // 使用完整的VSCode拖拽数据解析（与聊天工作区一致）
        console.log('[EditorChat] 开始解析VSCode拖拽数据...');
        const filePaths = await this.parseVSCodeDragData(event.dataTransfer);
        if (filePaths.length > 0) {
          console.log('[EditorChat] 解析出VSCode拖拽文件路径:', filePaths.length, '个');
          await this.handleVSCodeFilePaths(filePaths);
          return;
        }

        console.warn('[EditorChat] No files found in drop event');
        this.showErrorNotification('未检测到文件，请确保拖拽的是文件而不是文本');
        return;
      }

      console.log('[EditorChat] Files dropped:', files.map(f => ({ name: f.name, size: f.size })));

      // 检查当前是否有活跃会话
      const currentSession = this.getCurrentSessionInfo();
      if (!currentSession || !currentSession.id) {
        this.showSessionSelectionRequired();
        return;
      }

      // 显示确认对话框
      const confirmed = await this.confirmFileSend(files, currentSession);
      if (!confirmed) {
        return;
      }

      // 🔄 复用现有的文件发送逻辑
      await this.processDroppedFiles(files);
    },

    // 解析VSCode拖拽数据的完整逻辑（复用聊天工作区的解析方案）
    async parseVSCodeDragData(dataTransfer) {
      const filePaths = [];

      console.log('[EditorChat] 🔍 开始解析VSCode拖拽数据，可用类型:', Array.from(dataTransfer.types));

      // 方式1: VSCode内部URI列表 (多文件支持) - 最优先
      // 使用正确的VSCode数据类型
      const internalUriList = dataTransfer.getData('application/vnd.code.uri-list') || dataTransfer.getData('application/vnd.code.tree.resourcecontext');
      if (internalUriList) {
        console.log('[EditorChat] 🔗 获取到内部URI数据:', internalUriList);
        try {
          // 如果是URI列表格式，直接按行分割
          if (internalUriList.includes('\n') || internalUriList.includes('file://')) {
            const uris = internalUriList.split(/[\r\n]+/).filter(uri => uri.trim() && !uri.startsWith('#'));
            for (const uri of uris) {
              const path = uri.replace('file://', '');
              filePaths.push(path);
            }
          } else {
            // 尝试解析为JSON数组
            const parsedData = JSON.parse(internalUriList);
            if (Array.isArray(parsedData)) {
              for (const item of parsedData) {
                if (item.uri) {
                  const path = item.uri.replace('file://', '');
                  filePaths.push(path);
                } else if (typeof item === 'string') {
                  const path = item.replace('file://', '');
                  filePaths.push(path);
                }
              }
            }
          }
        } catch (e) {
          console.warn('[EditorChat] 解析内部URI数据失败:', e);
        }
      }

      // 方式2: VSCode资源列表
      if (filePaths.length === 0) {
        const resourcesData = dataTransfer.getData('application/vnd.code.resources');
        if (resourcesData) {
          console.log('[EditorChat] 🔗 获取到resources数据:', resourcesData);
          try {
            const resources = JSON.parse(resourcesData);
            if (Array.isArray(resources)) {
              for (const resource of resources) {
                if (typeof resource === 'string') {
                  const path = resource.replace('file://', '');
                  filePaths.push(path);
                } else if (resource.uri) {
                  const path = resource.uri.replace('file://', '');
                  filePaths.push(path);
                }
              }
            }
          } catch (e) {
            console.warn('[EditorChat] 解析resources数据失败:', e);
          }
        }
      }

      // 方式3: CodeFiles (文件路径数组) - 使用正确的类型名称
      if (filePaths.length === 0) {
        const codeFiles = dataTransfer.getData('codefiles') || dataTransfer.getData('application/vnd.code.files');
        if (codeFiles) {
          console.log('[EditorChat] 🔗 获取到CodeFiles:', codeFiles);
          try {
            const fileData = JSON.parse(codeFiles);
            if (Array.isArray(fileData)) {
              filePaths.push(...fileData);
            }
          } catch (e) {
            console.warn('[EditorChat] 解析CodeFiles失败:', e);
          }
        }
      }

      // 方式4: 标准text/uri-list (通常只有一个URI，但也可能有多个)
      if (filePaths.length === 0) {
        const uriList = dataTransfer.getData('text/uri-list');
        if (uriList) {
          console.log('[EditorChat] 🔗 获取到text/uri-list:', uriList);
          const uris = uriList.split(/[\r\n]+/).filter(uri => uri.trim() && !uri.startsWith('#'));
          for (const uri of uris) {
            const path = uri.replace('file://', '');
            filePaths.push(path);
          }
        }
      }

      // 方式5: 简单的text/plain格式（最后备选）
      if (filePaths.length === 0) {
        const plainText = dataTransfer.getData('text/plain');
        if (plainText) {
          console.log('[EditorChat] 🔗 获取到text/plain:', plainText);

          // 检查是否是文件路径格式
          if (plainText.includes('/') || plainText.includes('\\')) {
            // 检查是否是JSON格式的数据
            if (plainText.startsWith('{') || plainText.startsWith('[')) {
              try {
                const parsedData = JSON.parse(plainText);
                if (Array.isArray(parsedData)) {
                  filePaths.push(...parsedData);
                } else if (parsedData.path || parsedData.uri) {
                  filePaths.push(parsedData.path || parsedData.uri);
                }
              } catch (e) {
                console.log('[EditorChat] 不是JSON数据，按行分割处理');
              }
            }

            // 如果不是JSON，尝试按行分割
            if (filePaths.length === 0) {
              const lines = plainText.split('\n').filter(path => path.trim() && (path.includes('/') || path.includes('\\')));
              filePaths.push(...lines);
            }
          }
        }
      }

      // 清理路径格式
      const cleanPaths = filePaths
        .map(path => path.trim())
        .filter(path => path && path !== '')
        .map(path => path.replace(/^file:\/\//, '')) // 移除file://前缀
        .filter((path, index, arr) => arr.indexOf(path) === index); // 去重

      console.log('[EditorChat] 🎯 最终解析出文件路径:', cleanPaths.length, '个:', cleanPaths);
      return cleanPaths;
    },

    // 处理VSCode文件路径（优化版）
    async handleVSCodeFilePaths(filePaths) {
      console.log('[EditorChat] 🚀 处理VSCode文件路径:', filePaths.length, '个');

      if (filePaths.length === 0) {
        this.showErrorNotification('无法解析拖拽的文件路径');
        return;
      }

      // 🚀 使用SharedServicesAccessor直接从路径上传文件
      if (window.BoxIMComponents) {
        try {
          // 显示进度提示
          this.showFileProcessingProgress(filePaths.length);

          // 获取当前会话信息
          const sessionInfo = this.getCurrentSessionInfo();
          if (!sessionInfo) {
            throw new Error('未找到当前会话信息');
          }

          console.log('[EditorChat] 开始从路径批量上传文件:', filePaths);
          const results = await window.vscodeServices?.get('IChatWorkspaceService').uploadFiles(filePaths, sessionInfo);

          // 处理上传结果（Vue store同步已集成到uploadFileFromPath中）
          let successCount = 0;
          let failCount = 0;

          for (const result of results) {
            if (result.success) {
              successCount++;
              console.log('[EditorChat] ✅ 文件上传和消息同步成功:', result.message);
            } else {
              failCount++;
              console.error('[EditorChat] ❌ 文件上传失败:', result);
              this.$message && this.$message.error(result.message);
            }
          }

          // 显示总体结果
          if (failCount > 0) {
            console.error(`[EditorChat] ${failCount} 个文件上传失败`);
          }

          this.hideFileProcessingProgress();

        } catch (error) {
          console.error('[EditorChat] 批量路径文件上传失败:', error);
          this.hideFileProcessingProgress();
          this.showErrorNotification(`文件上传失败: ${error.message}`);
        }
      } else {
        this.showErrorNotification('VSCode服务不可用，无法处理文件拖拽');
      }
    },

    // 🎯 优化：使用SharedServicesAccessor的批量文件上传
    async processDroppedFiles(files) {
      console.log('[EditorChat] Processing dropped files...');

      // 过滤掉空文件
      const validFiles = files.filter(file => {
        if (file.size === 0) {
          this.$message && this.$message.warning(`文件 ${file.name} 为空，已跳过`);
          return false;
        }
        return true;
      });

      if (validFiles.length === 0) {
        console.log('[EditorChat] 没有有效文件需要上传');
        return;
      }

      try {
        // 显示进度提示
        this.showFileProcessingProgress(validFiles.length);

        // 获取当前会话信息
        const sessionInfo = this.getCurrentSessionInfo();
        if (!sessionInfo) {
          throw new Error('未找到当前会话信息');
        }

        // 🚀 使用SharedServicesAccessor的批量上传方法
        if (window.vscodeServices) {
          console.log('[EditorChat] 开始批量文件上传:', validFiles.length, '个文件');
          const results = await window.vscodeServices.get('IChatWorkspaceService').uploadFiles(validFiles, sessionInfo);

          // 处理上传结果
          let successCount = 0;
          let failCount = 0;

          for (const result of results) {
            if (result.success) {
              successCount++;
              console.log('[EditorChat] ✅ 文件上传成功:', result.message);
            } else {
              failCount++;
              console.error('[EditorChat] ❌ 文件上传失败:', result);
              this.$message && this.$message.error(result.message);
            }
          }

          // 显示总体结果
          if (failCount > 0) {
            console.error(`[EditorChat] ${failCount} 个文件上传失败`);
          }

        } else {
          // 降级：逐个处理文件
          console.log('[EditorChat] 降级为逐个处理文件');
          for (const file of validFiles) {
            console.log('[EditorChat] Processing file:', file.name);

            await this.handleFileSend({
              file: file,
              fileName: file.name,
              fileSize: file.size,
              fileType: file.type
            });
          }
        }

        this.hideFileProcessingProgress();

      } catch (error) {
        console.error('[EditorChat] File processing failed:', error);
        this.hideFileProcessingProgress();
        this.showErrorNotification(error.message);
      }
    },

    // 检查是否包含文件
    hasFiles(dataTransfer) {
      if (!dataTransfer) return false;

      // 检查files集合
      if (dataTransfer.files && dataTransfer.files.length > 0) {
        console.log('[EditorChat] Found files in dataTransfer.files');
        return true;
      }

      // 检查types
      if (dataTransfer.types) {
        const hasFileType = dataTransfer.types.includes('Files') ||
          dataTransfer.types.includes('application/x-vscode-file');
        if (hasFileType) {
          console.log('[EditorChat] Found file types in dataTransfer.types');
          return true;
        }
      }

      // 检查items
      if (dataTransfer.items && dataTransfer.items.length > 0) {
        for (let i = 0; i < dataTransfer.items.length; i++) {
          const item = dataTransfer.items[i];
          if (item.kind === 'file') {
            console.log('[EditorChat] Found file in dataTransfer.items');
            return true;
          }
        }
      }

      // 检查是否有文本数据（可能是VSCode文件路径）
      if (dataTransfer.types && dataTransfer.types.includes('text/plain')) {
        console.log('[EditorChat] Found text data, might be VSCode file paths');
        return true;
      }

      console.log('[EditorChat] No files detected in dataTransfer');
      return false;
    },

    // 显示拖拽提示
    showDragHint() {
      if (this.dragHintElement) return;

      const hint = document.createElement('div');
      hint.className = 'file-drag-hint';
      hint.innerHTML = `
        <div class="drag-hint-content">
          <div class="drag-hint-icon">📁</div>
          <div class="drag-hint-text">拖拽文件到此处发送到聊天</div>
          <div class="drag-hint-session">${this.getCurrentSessionInfo()?.name || '当前会话'}</div>
        </div>
      `;

      this.$el.appendChild(hint);
      this.dragHintElement = hint;
    },

    // 隐藏拖拽提示
    hideDragHint() {
      if (this.dragHintElement) {
        this.dragHintElement.remove();
        this.dragHintElement = null;
      }
    },

    // 添加拖拽样式
    addDragOverClass() {
      this.$el.classList.add('file-drag-over');
    },

    // 移除拖拽样式
    removeDragOverClass() {
      this.$el.classList.remove('file-drag-over');
    },

    // 确认文件发送
    async confirmFileSend(files, session) {
      const fileList = files.map(f => `• ${f.name} (${this.formatFileSize(f.size)})`).join('\n');

      if (this.$confirm) {
        try {
          await this.$confirm(
            `确认发送 ${files.length} 个文件到 "${session.name}"？\n\n${fileList}`,
            '确认文件发送',
            {
              confirmButtonText: '发送',
              cancelButtonText: '取消',
              type: 'info',
              modal: false
            }
          );
          return true;
        } catch {
          return false;
        }
      } else {
        // 降级到原生确认对话框
        return confirm(`确认发送 ${files.length} 个文件到 "${session.name}"？\n\n${fileList}`);
      }
    },

    // 显示会话选择提示
    showSessionSelectionRequired() {
      const message = '请先选择一个聊天会话，然后再拖拽文件';

      if (this.$message) {
        this.$message.warning(message);
      } else {
        alert(message);
      }
    },

    // 显示文件处理进度
    showFileProcessingProgress(fileCount) {
      console.log(`[EditorChat] 开始处理 ${fileCount} 个文件...`);
    },

    // 隐藏文件处理进度
    hideFileProcessingProgress() {
      if (this.processingMessage) {
        this.processingMessage.close();
        this.processingMessage = null;
      }
    },

    // 显示错误通知
    showErrorNotification(errorMessage) {
      const message = `❌ 文件发送失败: ${errorMessage}`;

      if (this.$message) {
        this.$message.error(message);
      } 
    },

    // 格式化文件大小
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    // 清理拖拽监听器
    cleanupFileDragDrop() {
      const dropZones = [
        this.$el,
        this.$el?.querySelector('.chat-box'),
        this.$el?.querySelector('.chat-page')
      ].filter(Boolean);

      dropZones.forEach(dropZone => {
        if (dropZone) {
          dropZone.removeEventListener('dragover', this.handleDragOver, { capture: true });
          dropZone.removeEventListener('dragenter', this.handleDragEnter, { capture: true });
          dropZone.removeEventListener('dragleave', this.handleDragLeave, { capture: true });
          dropZone.removeEventListener('drop', this.handleFileDrop, { capture: true });
        }
      });

      this.hideDragHint();
    },

    // 🔧 设置统一会话服务监听器
    setupUnifiedSessionListener() {
      console.log("[EditorChat] 🔧 开始设置统一会话服务监听器");

      try {
        let unifiedSessionService = null;

        // 多种方式尝试获取统一会话服务

        // 方式1: 通过$vscode服务获取
        if (this.$vscode && this.$vscode.get) {
          console.log("[EditorChat] 🔧 尝试通过$vscode.get获取统一会话服务...");
          unifiedSessionService = window.vscodeServices?.get('IUnifiedSessionService');
          console.log("[EditorChat] 🔧 通过$vscode.get获取结果:", !!unifiedSessionService);
        }

        // 方式2: 通过SharedServicesAccessor获取
        if (!unifiedSessionService && window.SharedServicesAccessor) {
          console.log("[EditorChat] 🔧 尝试通过SharedServicesAccessor获取统一会话服务...");
          unifiedSessionService = window.SharedServicesAccessor.get('IUnifiedSessionService');
          console.log("[EditorChat] 🔧 通过SharedServicesAccessor获取结果:", !!unifiedSessionService);
        }

        // 方式3: 通过全局VSCode服务获取
        if (!unifiedSessionService && window.vscode?.services) {
          console.log("[EditorChat] 🔧 尝试通过全局vscode.services获取统一会话服务...");
          unifiedSessionService = window.vscode.services.get('IUnifiedSessionService');
          console.log("[EditorChat] 🔧 通过全局vscode.services获取结果:", !!unifiedSessionService);
        }

        // 方式4: 直接从全局对象获取
        if (!unifiedSessionService && window.unifiedSessionService) {
          console.log("[EditorChat] 🔧 尝试从全局对象获取统一会话服务...");
          unifiedSessionService = window.unifiedSessionService;
          console.log("[EditorChat] 🔧 从全局对象获取结果:", !!unifiedSessionService);
        }

        console.log("[EditorChat] 🔧 最终统一会话服务获取结果:", {
          hasService: !!unifiedSessionService,
          serviceType: typeof unifiedSessionService,
          hasOnSessionChanged: !!unifiedSessionService?.onSessionChanged,
          hasGetAllSessions: !!unifiedSessionService?.getAllSessions
        });

        if (unifiedSessionService && unifiedSessionService.onSessionChanged) {
          console.log("[EditorChat] 🔧 统一会话服务正常，开始订阅事件...");

          // 订阅会话变更事件
          const disposable = unifiedSessionService.onSessionChanged(
            this.handleUnifiedSessionEvent.bind(this)
          );

          console.log("[EditorChat] 🔧 事件订阅结果:", {
            hasDisposable: !!disposable,
            disposableType: typeof disposable,
            hasDispose: !!disposable?.dispose
          });

          if (disposable && typeof disposable.dispose === 'function') {
            this.unifiedSessionUnsubscribe = () => disposable.dispose();
            console.log("[EditorChat] ✅ 已成功订阅统一会话事件");
          } else {
            console.warn("[EditorChat] ⚠️ 事件订阅返回的不是有效的disposable对象:", disposable);
            this.unifiedSessionUnsubscribe = null;
          }
        } else {
          console.warn("[EditorChat] ⚠️ 统一会话服务或onSessionChanged事件不可用");
          console.warn("[EditorChat] ⚠️ 尝试设置备用监听机制...");
          this.setupFallbackSessionListener();
          this.unifiedSessionUnsubscribe = null;
        }
      } catch (error) {
        console.error("[EditorChat] ❌ 设置统一会话监听器失败:", error);
        console.error("[EditorChat] ❌ 错误详情:", error.stack);
        console.warn("[EditorChat] ⚠️ 尝试设置备用监听机制...");
        this.setupFallbackSessionListener();
        this.unifiedSessionUnsubscribe = null;
      }

      console.log("[EditorChat] 🔧 统一会话服务监听器设置完成");
    },

    // 🔧 设置备用会话监听机制
    setupFallbackSessionListener() {
      console.log("[EditorChat] 🔧 设置备用会话监听机制...");

      try {
        // 备用方案1: 监听全局自定义事件
        const fallbackHandler = (event) => {
          console.log("[EditorChat] 🔔 收到备用会话事件:", event.detail);
          if (event.detail && event.detail.type === 'unified-session-activated') {
            this.handleUnifiedSessionEvent(event.detail);
          }
        };

        window.addEventListener('unified-session-activated', fallbackHandler);
        console.log("[EditorChat] ✅ 已设置全局事件监听器");

        this.unifiedSessionUnsubscribe = () => {
          window.removeEventListener('unified-session-activated', fallbackHandler);
          console.log("[EditorChat] 🔧 已清理备用事件监听器");
        };

        // 备用方案2: 定期检查会话状态变化
        let lastActiveSessionId = null;
        const pollInterval = setInterval(() => {
          try {
            let unifiedSessionService = null;

            // 尝试获取服务
            if (window.SharedServicesAccessor) {
              unifiedSessionService = window.SharedServicesAccessor.get('IUnifiedSessionService');
            }

            if (unifiedSessionService && typeof unifiedSessionService.getActiveSession === 'function') {
              const activeSession = unifiedSessionService.getActiveSession();
              const currentActiveSessionId = activeSession?.id;

              if (currentActiveSessionId && currentActiveSessionId !== lastActiveSessionId) {
                console.log("[EditorChat] 🔔 检测到活跃会话变化 (轮询):", currentActiveSessionId);
                lastActiveSessionId = currentActiveSessionId;

                // 模拟统一会话事件
                this.handleUnifiedSessionEvent({
                  type: 'unified-session-activated',
                  sessionId: currentActiveSessionId,
                  sessionType: activeSession.type,
                  session: activeSession
                });
              }
            }
          } catch (error) {
            // 静默处理轮询错误
          }
        }, 1000); // 每秒检查一次

        // 确保在组件销毁时清理轮询
        const originalCleanup = this.unifiedSessionUnsubscribe;
        this.unifiedSessionUnsubscribe = () => {
          clearInterval(pollInterval);
          if (originalCleanup) originalCleanup();
          console.log("[EditorChat] 🔧 已清理轮询机制");
        };

        console.log("[EditorChat] ✅ 已设置会话状态轮询机制");

      } catch (error) {
        console.error("[EditorChat] ❌ 设置备用监听机制失败:", error);
      }
    },

    // 🔧 处理统一会话服务事件
    handleUnifiedSessionEvent(event) {
      console.log('[EditorChat] 🔔 收到统一会话事件:', {
        eventType: event?.type,
        sessionId: event?.sessionId,
        sessionType: event?.sessionType,
        hasSession: !!event?.session,
        eventDetails: event
      });

      if (!event) {
        console.warn('[EditorChat] ⚠️ 收到空的统一会话事件');
        return;
      }

      if (event.type !== 'unified-session-activated') {
        console.log('[EditorChat] 🔧 非激活事件，忽略:', event.type);
        return;
      }

      console.log('[EditorChat] 🔔 处理会话激活事件，类型:', event.sessionType);

      // 根据会话类型处理
      if (event.sessionType === 'vue-im') {
        console.log('[EditorChat] 🔧 处理Vue IM会话激活');
        this.handleImSessionActivatedFromUnified(event);
      } else if (event.sessionType === 'react-ai') {
        console.log('[EditorChat] 🔧 处理React AI会话激活');
        this.handleAiSessionActivatedFromUnified(event);
      } else {
        console.warn('[EditorChat] ⚠️ 未知的会话类型:', event.sessionType);
      }
    },

    // 🔧 处理来自统一服务的IM会话激活
    handleImSessionActivatedFromUnified(event) {
      console.log('[EditorChat] 处理IM会话激活:', event.session?.title);

      const sessionData = event.session;
      if (!sessionData) {
        console.warn('[EditorChat] IM会话事件中没有会话数据');
        return;
      }

      // 获取targetId
      const targetId = sessionData.targetId || sessionData.sessionId || sessionData.realSessionRef;
      if (!targetId) {
        console.warn('[EditorChat] IM会话事件中没有有效的targetId:', sessionData);
        return;
      }

      // 防止重复激活同一会话
      const sessionId = `vue-im-${targetId}`;
      if (this.lastActivatedSessionId === sessionId) {
        console.log('[EditorChat] 🔧 防止重复激活，会话已是当前激活状态:', sessionId);
        return;
      }

      console.log('[EditorChat] 🔧 激活IM会话，targetId:', targetId);

      // 切换到IM模式
      this.selectedAIChat = null;
      this.isAIChat = false;

      // 通过targetId查找对应的聊天
      const targetIdStr = targetId.toString();
      const matchingChat = this.chatStore.chats?.find(
        chat => {
          const chatTargetId = (chat.targetId || chat.id)?.toString();
          return chatTargetId === targetIdStr;
        }
      );

      if (matchingChat) {
        const chatIndex = this.chatStore.chats.indexOf(matchingChat);
        console.log('[EditorChat] 🔧 找到匹配聊天，索引:', chatIndex, '会话名:', matchingChat.showName);

        // 检查当前激活状态，由于InboxSession已经激活，这里只需要确认状态
        console.log('[EditorChat] 🔧 当前activeChat:', this.chatStore.activeChat?.showName || 'null');
        console.log('[EditorChat] 🔧 期望activeChat:', matchingChat.showName);

        // 只要找到匹配的聊天，就认为激活成功，避免重复操作
        this.lastActivatedSessionId = sessionId;

        // 切换到IM模式
        this.selectedAIChat = null;
        this.isAIChat = false;

        console.log('[EditorChat] 🔧 IM会话状态已同步');
      } else {
        console.warn('[EditorChat] ❌ 未找到匹配的聊天，targetId:', targetId);
        console.log('[EditorChat] 🔧 可用聊天列表:', this.chatStore.chats?.map(c => ({
          targetId: c.targetId,
          id: c.id,
          showName: c.showName
        })));

        // 如果找不到匹配聊天，清除活跃状态
        this.$store.commit("clearActiveChat");
      }
    },

    // 🔧 处理来自统一服务的AI会话激活
    handleAiSessionActivatedFromUnified(event) {
      console.log('[EditorChat] 处理AI会话激活:', event.session?.title);

      const sessionData = event.session;
      if (!sessionData) {
        console.warn('[EditorChat] AI会话事件中没有会话数据');
        return;
      }

      // 切换到AI模式
      this.isAIChat = true;
      this.$store.commit("clearActiveChat"); // 清除人类聊天选择

      // 构造AI会话对象
      this.selectedAIChat = {
        id: sessionData.sessionId,
        aiThreadId: sessionData.sessionId,
        showName: sessionData.title || sessionData.showName || 'AI对话',
        title: sessionData.title || sessionData.showName || 'AI对话',
        summary: sessionData.summary || '',
        lastModified: sessionData.lastModified || new Date().toISOString()
      };

      console.log('[EditorChat] 🔧 AI会话已激活:', this.selectedAIChat.showName);
    },
  },
  mounted() {
    console.log('[EditorChat] 🔧 Component mounting...');

    // 检查事件总线
    console.log('[EditorChat] 🔧 Checking event bus:');
    console.log('- $bus:', !!this.$bus);
    console.log('- $eventBus:', !!this.$eventBus);

    // 设置通用事件监听
    this.setupEventListeners()

    // 🆕 添加文件拖拽支持
    this.setupFileDragDrop();

    // 设置文件事件监听
    if (this.$bus) {
      this.$bus.$on('chat:file:send', this.handleFileSend);
      this.$bus.$on('chat:file:upload', this.handleFileSend);
      console.log('[EditorChat] File event listeners registered on $bus');
    } else {
      console.warn('[EditorChat] $bus not available, cannot register file event listeners');
    }

    // 如果有$eventBus，也在那里监听
    if (this.$eventBus) {
      this.$eventBus.$on('chat:file:send', this.handleFileSend);
      this.$eventBus.$on('chat:file:upload', this.handleFileSend);
      console.log('[EditorChat] File event listeners registered on $eventBus');
    }

    console.log('[EditorChat] 🔧 Component mounted, all event listeners initialized');
  },
  beforeDestroy() {
    // 清理通用事件监听
    this.cleanupEventListeners()

    // 🔧 清理统一的拖拽监听器
    this.removeGlobalDragListeners();

    // 清理文件事件监听
    if (this.$bus) {
      this.$bus.$off('chat:file:send', this.handleFileSend);
      this.$bus.$off('chat:file:upload', this.handleFileSend);
    }
  },
  watch: {
    chatData: {
      handler(newChatData) {
        if (newChatData) {
          this.activateChatData(newChatData);
        }
      },
      immediate: true
    },
    'chatStore.activeChat': {
      handler(newActiveChat) {
        // 当激活的会话变化时，检测是否为PC终端
        this.detectPCTerminalChat();
      },
      deep: true
    }
  }
}
</script>

<style lang="scss">
.chat-page {
  height: 100% !important;
  background-color: #f1f1f1;
  position: relative;

  // 重写Element UI的el-container样式
  &.el-container {
    height: 100% !important;
    flex-direction: row;
  }

  // 🆕 文件拖拽样式
  &.file-drag-over {
    background: rgba(0, 122, 255, 0.1) !important;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: 3px dashed #007AFF;
      border-radius: 8px;
      pointer-events: none;
      z-index: 999;
    }
  }

  .chat-box {
    flex: 1;
    height: 100% !important;
    display: flex;
    flex-direction: column;
    min-height: 0; // 添加这行，确保flex子项能正确收缩

    // 重写Element UI的el-container样式
    &.el-container {
      height: 100% !important;
      flex-direction: column;
    }
    
    // 容器管理组件包装器
    .container-manager-wrapper {
      flex-shrink: 0;
      z-index: 10;
    }
  }

  .thinking-panel {
    height: 100% !important;
    border-left: 1px solid #e8eaec;
    background: white;

    // 重写Element UI的el-aside样式
    &.el-aside {
      height: 100% !important;
    }

    .panel-tabs {
      height: 100%;
      display: flex;
      flex-direction: column;

      .tabs-header {
        display: flex;
        border-bottom: 1px solid #e8eaec;
        background: #f8f9fa;

        .tab-btn {
          flex: 1;
          padding: 12px 8px;
          border: none;
          background: transparent;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          transition: all 0.2s;

          &:hover {
            background: #e9ecef;
          }

          &.active {
            background: white;
            border-bottom: 2px solid #007bff;
            color: #007bff;
          }

          .tab-icon {
            font-size: 16px;
          }

          .tab-label {
            font-size: 11px;
            font-weight: 500;
          }
        }
      }

      .tabs-content {
        flex: 1;
        overflow: hidden;

        .tab-panel {
          height: 100%;
          overflow-y: auto;
        }
      }
    }
  }

  .panel-toggle {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: #007bff;
    color: white;
    padding: 8px 12px;
    border-radius: 20px;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
    transition: all 0.3s ease;
    z-index: 100;

    &:hover {
      background: #0056b3;
      transform: translateY(-50%) scale(1.05);
    }

    .toggle-icon {
      margin-right: 6px;
    }

    .toggle-text {
      font-size: 12px;
      font-weight: 500;
    }
  }
}

// AI助手面板样式
.ai-assistant-panel {
  padding: 16px;
  height: 100%;
  overflow-y: auto;

  .assistant-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: #2c3e50;
    }

    .assistant-status {
      font-size: 12px;
      padding: 4px 8px;
      border-radius: 12px;
      font-weight: 500;

      &.active {
        background: #d4edda;
        color: #155724;
      }

      &.idle {
        background: #fff3cd;
        color: #856404;
      }

      &.analyzing {
        background: #cce5ff;
        color: #004085;
      }
    }
  }

  .quick-actions {
    margin-bottom: 24px;

    h4 {
      margin: 0 0 12px 0;
      font-size: 14px;
      font-weight: 600;
      color: #495057;
    }

    .action-buttons {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;

      .action-btn {
        padding: 8px 12px;
        border: 1px solid #dee2e6;
        border-radius: 6px;
        background: white;
        color: #495057;
        cursor: pointer;
        font-size: 12px;
        text-align: center;
        transition: all 0.2s;

        &:hover {
          background: #f8f9fa;
          border-color: #adb5bd;
        }
      }
    }
  }

  .ai-insights {
    margin-bottom: 24px;

    h4 {
      margin: 0 0 12px 0;
      font-size: 14px;
      font-weight: 600;
      color: #495057;
    }

    .insights-list {
      .insight-item {
        display: flex;
        gap: 12px;
        padding: 12px;
        margin-bottom: 8px;
        background: #f8f9fa;
        border-radius: 8px;
        border-left: 3px solid #dee2e6;

        &.priority-high {
          border-left-color: #dc3545;
        }

        &.priority-medium {
          border-left-color: #ffc107;
        }

        &.priority-low {
          border-left-color: #28a745;
        }

        .insight-icon {
          font-size: 16px;
          flex-shrink: 0;
        }

        .insight-content {
          flex: 1;

          .insight-title {
            font-weight: 500;
            font-size: 13px;
            color: #2c3e50;
            margin-bottom: 4px;
          }

          .insight-description {
            font-size: 12px;
            color: #6c757d;
            line-height: 1.4;
          }
        }
      }
    }
  }

  .learning-feedback {
    h4 {
      margin: 0 0 12px 0;
      font-size: 14px;
      font-weight: 600;
      color: #495057;
    }

    .feedback-form {
      .feedback-input {
        width: 100%;
        min-height: 80px;
        padding: 8px 12px;
        border: 1px solid #dee2e6;
        border-radius: 6px;
        resize: vertical;
        font-size: 12px;
        line-height: 1.4;
        margin-bottom: 8px;

        &:focus {
          outline: none;
          border-color: #007bff;
        }
      }

      .submit-btn {
        width: 100%;
        padding: 8px 12px;
        border: 1px solid #007bff;
        border-radius: 6px;
        background: #007bff;
        color: white;
        cursor: pointer;
        font-size: 12px;
        transition: all 0.2s;

        &:hover {
          background: #0056b3;
        }
      }
    }
  }
}

// 设置面板样式
.settings-panel {
  padding: 16px;
  height: 100%;
  overflow-y: auto;

  .settings-header {
    margin-bottom: 20px;

    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: #2c3e50;
    }
  }

  .settings-sections {
    .setting-section {
      margin-bottom: 24px;

      h4 {
        margin: 0 0 12px 0;
        font-size: 14px;
        font-weight: 600;
        color: #495057;
        border-bottom: 1px solid #e9ecef;
        padding-bottom: 6px;
      }

      .setting-item {
        margin-bottom: 12px;

        .setting-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: #495057;
          cursor: pointer;

          input[type="checkbox"] {
            margin: 0;
          }

          select {
            margin-left: auto;
            padding: 4px 8px;
            border: 1px solid #dee2e6;
            border-radius: 4px;
            font-size: 12px;
          }
        }
      }
    }
  }

  .settings-actions {
    display: flex;
    gap: 8px;
    margin-top: 20px;

    .reset-btn,
    .save-btn {
      flex: 1;
      padding: 8px 12px;
      border: 1px solid #dee2e6;
      border-radius: 6px;
      cursor: pointer;
      font-size: 12px;
      text-align: center;
      transition: all 0.2s;
    }

    .reset-btn {
      background: white;
      color: #6c757d;

      &:hover {
        background: #f8f9fa;
      }
    }

    .save-btn {
      background: #28a745;
      color: white;
      border-color: #28a745;

      &:hover {
        background: #218838;
      }
    }
  }
}

// 空状态样式
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  min-height: 100vh; // 确保至少占满视口高度
  position: relative;
  
  .empty-content {
    text-align: center;
    color: #6c757d;
    padding: 20px; // 添加内边距提供更好的视觉效果

    .empty-icon {
      font-size: 48px;
      margin-bottom: 16px;
      display: block; // 确保图标独占一行
    }

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: #333;
    }

    p {
      margin: 8px 0 0 0;
      font-size: 14px;
      line-height: 1.5; // 改善文字行高
    }
  }
}

// 🆕 拖拽提示浮层样式
.file-drag-hint {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  pointer-events: none;

  .drag-hint-content {
    background: white;
    border-radius: 12px;
    padding: 32px;
    text-align: center;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    transform: scale(0.95);
    animation: dragHintShow 0.2s ease-out forwards;

    .drag-hint-icon {
      font-size: 48px;
      margin-bottom: 16px;
    }

    .drag-hint-text {
      font-size: 18px;
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 8px;
    }

    .drag-hint-session {
      font-size: 14px;
      color: #6c757d;
      padding: 6px 12px;
      background: #f8f9fa;
      border-radius: 6px;
      display: inline-block;
    }
  }
}

@keyframes dragHintShow {
  from {
    opacity: 0;
    transform: scale(0.9);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
